import { useMeasurementConversion } from '@app/utils/hooks';
import { customerKeys } from '@data/constants';
import { useUpdateHouse } from '@data/hooks';
import { type HouseInput, HouseSchema, type HouseValidator } from '@data/utils/validation';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import {
  AntFlex,
  AntForm,
  AntSpace,
  AntTypography,
  Drawer,
  type DrawerProps,
  FormItem,
  Input,
} from '@ui/components';
import { Button } from '@ui/components';
import TextArea from 'antd/es/input/TextArea';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import type { HouseDataType } from '../Tabs/CustomerLocations';

interface EditHouseDrawerProps extends DrawerProps {
  houseData: HouseDataType;
}

export const EditHouseDrawer = ({ toggleIsOpen, houseData, ...props }: EditHouseDrawerProps) => {
  const { t } = useTranslation();
  const { customerId } = useParams();
  const queryClient = useQueryClient();

  const { Text } = AntTypography;

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<HouseInput, unknown, HouseValidator>({
    mode: 'onBlur',
    resolver: zodResolver(HouseSchema),
    values: {
      name: houseData.house.name,
      size: houseData.house.size,
      description: houseData.house.description,
    },
  });

  const { mutateAsync: updateHouse } = useUpdateHouse();

  const { areaMeasurementUnit, toSquareMeters } = useMeasurementConversion();

  const onSubmit = async ({ size, ...values }: HouseValidator) => {
    if (!houseData || !customerId) return;
    const { customerLocationId, house } = houseData;
    await updateHouse({
      id: customerId,
      customerLocationId,
      houseId: house.id,
      updateHouseBody: { ...values, size: toSquareMeters(size) },
    });
    await queryClient.invalidateQueries({ queryKey: customerKeys.detail(customerId) });
    toggleIsOpen();
  };

  return (
    <Drawer
      title={
        <AntFlex align="center" justify="space-between">
          <Text>{t('edit_house')}</Text>
          <AntSpace>
            <Button
              type="primary"
              htmlType="submit"
              disabled={!isValid}
              onClick={handleSubmit(onSubmit)}
              style={{ minWidth: 100 }}
            >
              {t('actions.save')}
            </Button>
          </AntSpace>
        </AntFlex>
      }
      onClose={toggleIsOpen}
      {...props}
    >
      <AntForm name="createHatchery" layout="vertical">
        <FormItem required name="name" label={t('house_name')} error={errors.name?.message}>
          <Controller control={control} name="name" render={({ field }) => <Input {...field} />} />
        </FormItem>

        <FormItem
          name="size"
          label={t('net_area_size', { unit: areaMeasurementUnit })}
          error={errors.size?.message}
        >
          <Controller control={control} name="size" render={({ field }) => <Input {...field} />} />
        </FormItem>

        <FormItem name="description" label={t('description')} error={errors.description?.message}>
          <Controller
            control={control}
            name="description"
            render={({ field }) => <TextArea showCount maxLength={500} {...field} />}
          />
        </FormItem>
      </AntForm>
    </Drawer>
  );
};
